import { memo, useReducer } from "react";
import { useSearchParams } from "react-router-dom";

import styled from "styled-components";

import CircularProgress from "@mui/material/CircularProgress";

import asyncOrSwim from "utility/async_or_swim";
import { captureSentryException } from "utility/capture_sentry_exception";
import { unauthenticatedPostFetch } from "utility/fetch";

import useAnalytics from "effect/use_analytics";

import { LoginContainer } from "./Component/Container";
import { LoginError } from "./Component/Error";
import { LoginForm } from "./Component/Form";

// -----------------------------------------------------------------------------

const LoginPage = memo(function LoginPage(): JSX.Element {
  useAnalytics("Login User");

  //
  const [
    { errorMessage, checkEmail, contactingServer, hasSSORedirect },
    setState,
  ] = useReducer((state: any, action: any) => ({ ...state, ...action }), {
    errorMessage: useErrorMessageFromURL(),
    checkEmail: false,
    hasSSORedirect: false,
    contactingServer: false,
  });

  //
  switch (true) {
    case !!errorMessage:
      return (
        <LoginContainer>
          <LoginError errorMessage={errorMessage} reset={reset(setState)} />
        </LoginContainer>
      );

    case contactingServer:
      return (
        <LoginContainer noMaxWidth>
          <CircularProgress />
        </LoginContainer>
      );

    case hasSSORedirect:
      return (
        <LoginContainer>
          You are being redirected to your SSO provider to login. Please contact
          your admin if you experience any issues.
        </LoginContainer>
      );

    case checkEmail:
      return (
        <LoginContainer noMaxWidth>
          <Centered>
            We emailed a login link to your email.
            <br />
            You can close this window.
          </Centered>
        </LoginContainer>
      );

    default:
      return (
        <LoginContainer>
          <LoginForm login={login(setState)} />
        </LoginContainer>
      );
  }
});

export default LoginPage;

// -----------------------------------------------------------------------------

function login(setState: Function): (emailAddress: string) => void {
  return (emailAddress: string): void => {
    const emailAddressParsed = emailAddress.trim();
    if (emailAddressParsed.length < 1) {
      return; // eslint-disable-line
    }

    //
    setState({ contactingServer: true });
    asyncOrSwim(
      async () => {
        const responsePayload = await unauthenticatedPostFetch<{
          error: boolean;
          message: string;
          redirectURL: string;
        }>(
          "api/auth/loginUser",
          {
            email: emailAddressParsed,
          },
          [200, 400]
        );

        //
        switch (true) {
          case responsePayload.error:
            return setState({
              contactingServer: false,
              checkEmail: false,
              errorMessage: responsePayload.error,
            });

          case !!responsePayload.redirectURL:
            // nosemgrep: javascript.browser.security.open-redirect.js-open-redirect
            window.location.href = responsePayload.redirectURL;

            //
            return setState({
              contactingServer: false,
              checkEmail: false,
              hasSSORedirect: true,
              errorMessage: "",
            });

          case responsePayload.message === "Email sent":
            return setState({
              contactingServer: false,
              checkEmail: true,
              errorMessage: "",
            });

          default:
            throw new Error("Email wasn't sent");
        }
      },
      (error) => {
        captureSentryException(error, "Failed to login");
        setState({
          contactingServer: false,
          checkEmail: false,
          errorMessage: "signin_error",
        });
      }
    );
  };
}

function reset(setState: Function): () => void {
  return () =>
    setState({
      contactingServer: false,
      checkEmail: false,
      errorMessage: "",
    });
}

function useErrorMessageFromURL(): string {
  const [searchParams] = useSearchParams();

  //
  return new URLSearchParams(searchParams).get("error_msg") ?? "";
}

// -----------------------------------------------------------------------------

const Centered = styled.div`
  text-align: center;
`;
